import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { useInView } from "@/components/Reveal";

const DEFAULT_TRAITS = [
  { label: "Leader", value: 0.35 },
  { label: "Analitico", value: 0.55 },
  { label: "Creativo", value: 0.9 },
  { label: "Collaborativo", value: 0.75 },
  { label: "Organizzato", value: 0.5 },
  { label: "Comunicativo", value: 0.7 },
];
const CX = 170, CY = 148, R = 92;
const angle = (i) => ((-90 + i * 60) * Math.PI) / 180;
const pt = (i, r) => [CX + Math.cos(angle(i)) * r, CY + Math.sin(angle(i)) * r];
const ring = (f) => DEFAULT_TRAITS.map((_, i) => pt(i, R * f).join(",")).join(" ");

const useGrow = (active) => {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setP(1);
      return;
    }
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const k = Math.min((t - t0) / 1400, 1);
      setP(1 - Math.pow(1 - k, 3));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return p;
};

const AxisLabel = ({ i, label }) => {
  const [x, y] = pt(i, R + 18);
  const c = Math.cos(angle(i));
  const anchor = c > 0.1 ? "start" : c < -0.1 ? "end" : "middle";
  const dy = i === 0 ? -2 : i === 3 ? 10 : 4;
  return (
    <text x={x} y={y + dy} textAnchor={anchor} fill="#8a8f98" fontSize="11.5" fontFamily="Inter, sans-serif" fontWeight="500">
      {label}
    </text>
  );
};

const Tooltip = ({ trait, i }) => {
  const [x, y] = pt(i, R * trait.value);
  const text = `${trait.label} · ${Math.round(trait.value * 100)}%`;
  const wBox = text.length * 6.6 + 18;
  const tx = Math.min(Math.max(x - wBox / 2, 4), 340 - wBox - 4);
  return (
    <g pointerEvents="none" data-testid="radar-tooltip">
      <rect x={tx} y={y - 38} width={wBox} height={24} rx={7} fill="rgba(5,5,5,0.92)" stroke="#00e5ff" strokeOpacity="0.6" />
      <text x={tx + wBox / 2} y={y - 22} textAnchor="middle" fill="#fff" fontSize="11" fontFamily="Inter, sans-serif" fontWeight="600">
        {text}
      </text>
    </g>
  );
};

export const RadarChart = ({ separator = "·", traits = DEFAULT_TRAITS }) => {
  const TRAITS = traits;
  const [first, second] = [...traits].sort((a, b) => b.value - a.value);
  const [ref, inView] = useInView(0.3);
  const p = useGrow(inView);
  const [hover, setHover] = useState(null);
  const pts = TRAITS.map((t, i) => pt(i, R * t.value * p));

  return (
    <div ref={ref} className="rounded-2xl border border-[#1e2024] bg-black/40 p-5" data-testid="radar-chart">
      <div className="flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.2em] uppercase text-cyan">
        <Activity className="h-3.5 w-3.5" strokeWidth={2} />
        Profilo psico-attitudinale
      </div>
      <svg viewBox="0 0 340 290" className="w-full h-auto mt-2 overflow-visible" role="img" aria-label="Grafico radar del profilo psico-attitudinale">
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <polygon key={f} points={ring(f)} fill="none" stroke="#ffffff" strokeOpacity={f === 1 ? 0.14 : 0.07} />
        ))}
        {TRAITS.map((_, i) => {
          const [x, y] = pt(i, R);
          return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="#ffffff" strokeOpacity="0.07" />;
        })}
        <polygon
          points={pts.map((q) => q.join(",")).join(" ")}
          fill="rgba(0, 190, 210, 0.22)"
          stroke="#00e5ff"
          strokeWidth="1.8"
          strokeLinejoin="round"
          style={{ filter: "drop-shadow(0 0 6px rgba(0,229,255,0.45))" }}
        />
        {pts.map(([x, y], i) => (
          <g
            key={TRAITS[i].label}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            onTouchStart={() => setHover(i)}
            style={{ cursor: "pointer" }}
            data-testid={`radar-vertex-${TRAITS[i].label.toLowerCase()}`}
          >
            <circle cx={x} cy={y} r="12" fill="transparent" />
            <circle cx={x} cy={y} r={hover === i ? 5.5 : 3.8} fill="#00e5ff" style={{ filter: "drop-shadow(0 0 5px #00e5ff)", transition: "r 0.2s ease" }} />
          </g>
        ))}
        {TRAITS.map((t, i) => (
          <AxisLabel key={t.label} i={i} label={t.label} />
        ))}
        {hover !== null && <Tooltip trait={TRAITS[hover]} i={hover} />}
      </svg>
      <div className="mt-3 pt-4 border-t border-[#1e2024] flex items-center justify-between gap-3 text-[11.5px]">
        <p className="text-titanium" data-testid="radar-dominant-trait">
          Tratto dominante <span className="font-semibold text-cyan">{first.label}</span> {separator} poi{" "}
          <span className="font-semibold text-white">{second.label}</span>
        </p>
        <span className="text-titanium whitespace-nowrap" data-testid="radar-date">18 settembre 2026</span>
      </div>
    </div>
  );
};
